import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Switch } from '@mui/material';
import { theme } from '../constants';
import { dateBounds, effectiveDateMin, formatFieldValue, isBlank, validateForm } from '../function';
import { InfoIcon } from './Icons';

const inputStyle = (invalid) => ({
  width: '100%',
  border: `1px solid ${invalid ? theme.danger : theme.border}`,
  borderRadius: 8,
  padding: '8px 11px',
  fontSize: 13,
  fontFamily: theme.font,
  color: theme.text,
  outline: 'none',
  background: theme.surface,
});

const FieldShell = ({ field, error, hint, required, children }) => (
  <div style={{ flex: '1 1 150px', minWidth: 140 }}>
    <label
      style={{
        display: 'block',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: theme.textFaint,
        marginBottom: 5,
      }}
    >
      {field.label}
      {required && <span style={{ color: theme.danger, marginLeft: 2 }}>*</span>}
    </label>
    {children}
    {error ? (
      <div style={{ fontSize: 11, color: theme.danger, marginTop: 4, fontWeight: 600 }}>{error}</div>
    ) : (
      hint && <div style={{ fontSize: 10.5, color: theme.textFaint, marginTop: 4, fontWeight: 600 }}>{hint}</div>
    )}
  </div>
);

/**
 * Editor for one device's schedule section. Every input is generated from
 * `section.fields`, so both columns share this one component and the hour /
 * minute and day / month / year splits stay hidden behind a single control.
 */
function ScheduleEditModal({ open, onClose, target, onSave, saving }) {
  const { device, section, values, pending } = target || {};
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});

  // Reseed whenever a different cell is opened, so a cancelled edit is dropped.
  useEffect(() => {
    if (open && section) {
      setForm({ ...values });
      setErrors({});
    }
  }, [open, section, values]);

  const dirty = useMemo(() => JSON.stringify({ ...values }) !== JSON.stringify(form), [values, form]);

  if (!section) return null;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSave = () => {
    const found = validateForm(section, form, values);
    if (Object.keys(found).some((key) => found[key])) {
      setErrors(found);
      return;
    }
    onSave(form);
  };

  const renderField = (field) => {
    const value = form[field.key];
    const error = errors[field.key];

    if (field.type === 'timestamp') {
      return (
        <FieldShell key={field.key} field={field}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.textMuted, padding: '8px 0' }}>
            {isBlank(value) ? 'Never' : formatFieldValue(field, value)}
          </div>
        </FieldShell>
      );
    }

    if (field.type === 'boolean') {
      return (
        <FieldShell key={field.key} field={field} error={error}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Switch size="small" checked={!!value} onChange={(event) => setField(field.key, event.target.checked)} />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: value ? theme.success : theme.textMuted }}>
              {value ? 'On' : 'Off'}
            </span>
          </div>
        </FieldShell>
      );
    }

    if (field.type === 'date') {
      const { max } = dateBounds(field);
      // Floored by the start date once one is picked, so the calendar itself
      // cannot produce a backwards window.
      const min = effectiveDateMin(field, form);
      const hint =
        min || max
          ? `${min ? formatFieldValue(field, min) : 'Any'} – ${max ? formatFieldValue(field, max) : 'any'}`
          : null;

      return (
        <FieldShell key={field.key} field={field} error={error} hint={hint} required>
          <input
            type="date"
            value={value ?? ''}
            min={min}
            max={max}
            onChange={(event) => setField(field.key, event.target.value)}
            style={inputStyle(!!error)}
          />
        </FieldShell>
      );
    }

    return (
      <FieldShell key={field.key} field={field} error={error} required>
        <input
          type="time"
          value={value ?? ''}
          onChange={(event) => setField(field.key, event.target.value)}
          style={inputStyle(!!error)}
        />
      </FieldShell>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { borderRadius: 14, fontFamily: theme.font } }}
    >
      <DialogTitle sx={{ fontFamily: theme.font, fontWeight: 700, fontSize: 15.5, paddingBottom: 0.5 }}>
        {section.label}
        <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, marginTop: 2 }}>
          {device?.name}
          {device?.hasName ? ` · ${device.deviceId}` : ''}
        </div>
      </DialogTitle>

      <DialogContent sx={{ paddingTop: '8px !important' }}>
        <div
          style={{
            display: 'flex',
            gap: 8,
            padding: '9px 11px',
            borderRadius: 9,
            background: section.accentSoft,
            color: section.accent,
            fontSize: 11.5,
            fontWeight: 600,
            lineHeight: '16px',
            marginBottom: 16,
          }}
        >
          <span style={{ display: 'flex', flexShrink: 0, marginTop: 1 }}>
            <InfoIcon size={13} />
          </span>
          {section.hint}
        </div>

        {pending && (
          <div
            style={{
              padding: '9px 11px',
              borderRadius: 9,
              background: 'rgba(245,158,11,0.12)',
              color: '#b45309',
              fontSize: 11.5,
              fontWeight: 600,
              lineHeight: '16px',
              marginTop: -6,
              marginBottom: 16,
            }}
          >
            A change is already queued and the device has not applied it yet. These are the queued values — saving
            replaces them.
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>{section.fields.map(renderField)}</div>

        <div style={{ fontSize: 11, color: theme.textFaint, marginTop: 14, fontWeight: 600 }}>
          <span style={{ color: theme.danger }}>*</span> All fields are required.
        </div>
      </DialogContent>

      <DialogActions sx={{ padding: '10px 20px 18px', gap: 1 }}>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          style={{
            border: `1px solid ${theme.border}`,
            background: theme.surface,
            color: theme.textMuted,
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 13,
            fontWeight: 700,
            fontFamily: theme.font,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty}
          style={{
            border: 'none',
            background: saving || !dirty ? '#c7d0e8' : theme.accent,
            color: '#fff',
            borderRadius: 8,
            padding: '8px 18px',
            fontSize: 13,
            fontWeight: 700,
            fontFamily: theme.font,
            cursor: saving || !dirty ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </DialogActions>
    </Dialog>
  );
}

export default ScheduleEditModal;
